import React, { useEffect, useState } from "react";
import { Calendar, Download, FileSpreadsheet, FileText, Home, MapPin, Users, LogIn, LogOut, TrendingUp, BarChart3, Clock, Footprints, Car, Bike, UserCheck} from "lucide-react";
import api from "../api/axios";
import { aISO, mostrarDia } from "../utils/fecha";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Card } from "./shared/Card";
import { Button } from "./shared/Button";
import { Table } from "./shared/Table";
import { DatePicker } from "./shared/DatePicker";

const styles = {
  container: {
    marginTop: '2rem',
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  dateGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--text)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  dateInput: {
    padding: '0.625rem 1rem',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg)',
    fontSize: '0.875rem',
    color: 'var(--text)',
    cursor: 'pointer',
  },
  exportButtons: {
    display: 'flex',
    gap: '0.5rem',
    marginLeft: 'auto',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  statCard: {
    padding: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--text)',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: 'var(--textSecondary)',
  },
  chartContainer: {
    marginTop: '2rem',
    padding: '1rem',
    backgroundColor: 'var(--bgSecondary)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: 'var(--textSecondary)',
  },
};

export default function VisitSummary() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  const [fromDate, setFromDate] = useState(aISO(firstDayOfMonth));
  const [toDate, setToDate] = useState(aISO(lastDayOfMonth));
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalStats, setTotalStats] = useState({
    totalVisits: 0,
    totalEntries: 0,
    totalExits: 0,
    totalHouses: 0,
  });

  const exportToExcel = () => {
    const exportData = summary.map(item => ({
      'Polígono': item.polygon,
      'Casa': item.houseNumber,
      'Total Visitas': item.total,
      'A pie': item.aPie,
      'Vehículos': item.vehiculos,
      'Motos': item.motos,
      'Entradas': item.entered,
      'Salidas': item.exited,
      'Dentro': item.dentro,
      'Pendientes': item.pendientes,
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resumen de Visitas");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, `ResumenVisitas_${fromDate}_a_${toDate}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text("Resumen de Visitas por Casa", 14, 20);
    doc.setFontSize(12);
    /* `fromDate` y `toDate` son los `YYYY-MM-DD` de los filtros, o sea días de
     * calendario. Sin `timeZone`, `toLocaleDateString` usa la zona del
     * navegador y en UTC-6 imprimía el día ANTERIOR en los dos extremos del
     * rango: el PDF que se lleva la directiva decía un período que no era el
     * que se pidió. */
    doc.text(`Período: ${mostrarDia(fromDate, { dateStyle: 'medium' })} - ${mostrarDia(toDate, { dateStyle: 'medium' })}`, 14, 28);
    
    // Stats summary
    doc.setFontSize(10);
    doc.text(`Total de Visitas: ${totalStats.totalVisits}`, 14, 36);
    doc.text(`Total de Entradas: ${totalStats.totalEntries}`, 70, 36);
    doc.text(`Total de Salidas: ${totalStats.totalExits}`, 126, 36);
    doc.text(`Casas con Visitas: ${totalStats.totalHouses}`, 182, 36);
    
    // Table
    autoTable(doc, {
      startY: 42,
      head: [["Polígono", "Casa", "Total", "A pie", "Veh.", "Motos", "Entradas", "Salidas", "Dentro", "Pend."]],
      body: summary.map((item) => [
        item.polygon,
        item.houseNumber,
        item.total,
        item.aPie,
        item.vehiculos,
        item.motos,
        item.entered,
        item.exited,
        item.dentro,
        item.pendientes,
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [59, 130, 246] },
    });
    
    doc.save(`ResumenVisitas_${fromDate}_a_${toDate}.pdf`);
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/visits/by-date?from=${fromDate}&to=${toDate}`);
      const visits = res.data.visits || res.data || [];
      const grouped = {};

      visits.forEach((v) => {
        const key = `${v.polygon}-${v.houseNumber}`;
        if (!grouped[key]) {
          grouped[key] = {
            houseNumber: v.houseNumber,
            polygon: v.polygon,
            total: 0,
            entered: 0,
            exited: 0,
            // Pendiente es quien NO ha entrado: `Pending`, y nada más. Esto
            // se calculaba como `total - exited`, así que contaba también al
            // que ya está adentro y a los pases denegados, revocados y
            // vencidos; el resumen decía 7 donde el panel decía 3, y la
            // directiva se llevaba ese número en el Excel. `AwaitingApproval`
            // tampoco es pendiente: espera el permiso del residente, no la
            // llegada.
            pendientes: 0,
            dentro: 0,
            // Cuánto de lo que entra a cada casa es tráfico de vehículos.
            // Es la cifra con la que se decide si una caseta necesita una
            // pluma, un carril o un guardia más.
            aPie: 0,
            vehiculos: 0,
            motos: 0,
          };
        }
        grouped[key].total += 1;
        if (v.arrivalMode === "vehicle") grouped[key].vehiculos += 1;
        else if (v.arrivalMode === "motorcycle") grouped[key].motos += 1;
        else grouped[key].aPie += 1;
        // «Entradas» cuenta a todo el que entró en el período, haya salido
        // o no; «Dentro» sólo a quien sigue adentro.
        if (v.status === "Entered" || v.status === "Exited")
          grouped[key].entered += 1;
        if (v.status === "Exited") grouped[key].exited += 1;
        if (v.status === "Entered") grouped[key].dentro += 1;
        if (v.status === "Pending") grouped[key].pendientes += 1;
      });

      const summaryData = Object.values(grouped);
      setSummary(summaryData);
      
      // Calculate total stats
      const stats = summaryData.reduce((acc, item) => {
        acc.totalVisits += item.total;
        acc.totalEntries += item.entered;
        acc.totalExits += item.exited;
        acc.totalHouses++;
        return acc;
      }, { totalVisits: 0, totalEntries: 0, totalExits: 0, totalHouses: 0 });
      
      setTotalStats(stats);
    } catch (err) {
      console.error("Error al cargar resumen", err);
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, [fromDate, toDate]);

  const columns = [
    {
      key: 'polygon',
      label: 'Polígono',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MapPin size={14} style={{ color: 'var(--textTertiary)' }} />
          <span style={{ fontWeight: '500' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'houseNumber',
      label: 'Casa',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Home size={14} style={{ color: 'var(--textTertiary)' }} />
          {value}
        </div>
      ),
    },
    {
      key: 'total',
      label: 'Total Visitas',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={14} style={{ color: 'var(--primary)' }} />
          <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{value}</span>
        </div>
      ),
    },
    {
      /* El desglose por forma de llegada, en una sola columna: tres columnas
         de números sueltos no se leen, y lo que interesa es la proporción. */
      key: 'aPie',
      label: 'Cómo llegaron',
      render: (_, fila) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8125rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="A pie">
            <Footprints size={14} style={{ color: 'var(--text-tertiary)' }} />
            {fila.aPie}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="En vehículo">
            <Car size={14} style={{ color: 'var(--gold-dark)' }} />
            {fila.vehiculos}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="En moto">
            <Bike size={14} style={{ color: 'var(--gold-dark)' }} />
            {fila.motos}
          </span>
        </div>
      ),
    },
    {
      key: 'entered',
      label: 'Entradas',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <LogIn size={14} style={{ color: 'var(--success)' }} />
          <span style={{ color: 'var(--success)' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'exited',
      label: 'Salidas',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <LogOut size={14} style={{ color: 'var(--info)' }} />
          <span style={{ color: 'var(--info)' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'dentro',
      label: 'Dentro',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <UserCheck size={14} style={{ color: 'var(--gold-dark)' }} />
          <span style={{ color: 'var(--gold-dark)' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'pendientes',
      label: 'Pendientes',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Clock size={14} style={{ color: 'var(--warning)' }} />
          <span style={{ color: 'var(--warning)', fontWeight: '500' }}>{value}</span>
        </div>
      ),
    },
  ];

  return (
    <div style={styles.container}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'var(--primaryBg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <BarChart3 size={24} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text)' }}>
              Resumen de Visitas por Casa
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--textSecondary)' }}>
              Análisis detallado de visitas por polígono y casa
            </p>
          </div>
        </div>

        <div style={styles.controls}>
          <div style={{ width: '180px' }}>
            <label style={styles.label}>
              <Calendar size={16} />
              Desde:
            </label>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              placeholder="Fecha inicial"
              maxDate={new Date()}
            />
          </div>
          
          <div style={{ width: '180px' }}>
            <label style={styles.label}>
              <Calendar size={16} />
              Hasta:
            </label>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              placeholder="Fecha final"
              maxDate={new Date()}
              minDate={fromDate ? new Date(fromDate) : undefined}
            />
          </div>
          
          <div style={styles.exportButtons}>
            <Button
              onClick={exportToExcel}
              variant="secondary"
              leftIcon={<FileSpreadsheet size={16} />}
            >
              Excel
            </Button>
            <Button
              onClick={exportToPDF}
              variant="secondary"
              leftIcon={<FileText size={16} />}
            >
              PDF
            </Button>
          </div>
        </div>

        <div style={styles.stats}>
          <Card style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: 'var(--primaryBg)', color: 'var(--primary)' }}>
              <Users size={20} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{totalStats.totalVisits}</div>
              <div style={styles.statLabel}>Total Visitas</div>
            </div>
          </Card>

          <Card style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: 'var(--successBg)', color: 'var(--success)' }}>
              <LogIn size={20} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{totalStats.totalEntries}</div>
              <div style={styles.statLabel}>Entradas</div>
            </div>
          </Card>

          <Card style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: 'var(--infoBg)', color: 'var(--info)' }}>
              <LogOut size={20} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{totalStats.totalExits}</div>
              <div style={styles.statLabel}>Salidas</div>
            </div>
          </Card>

          <Card style={styles.statCard}>
            <div style={{ ...styles.statIcon, backgroundColor: 'var(--warningBg)', color: 'var(--warning)' }}>
              <Home size={20} />
            </div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{totalStats.totalHouses}</div>
              <div style={styles.statLabel}>Casas</div>
            </div>
          </Card>
        </div>

        {loading ? (
          <div style={styles.emptyState}>
            <div className="animate-spin" style={{
              width: '2rem',
              height: '2rem',
              border: '2px solid var(--border)',
              borderTopColor: 'var(--primary)',
              borderRadius: '50%',
              margin: '0 auto',
            }}></div>
            <p style={{ marginTop: '1rem' }}>Cargando resumen...</p>
          </div>
        ) : summary.length === 0 ? (
          <div style={styles.emptyState}>
            <TrendingUp size={48} style={{ margin: '0 auto 1rem', color: 'var(--textTertiary)' }} />
            <h3 style={{ marginBottom: '0.5rem' }}>No hay datos</h3>
            <p>No se encontraron visitas en el período seleccionado</p>
          </div>
        ) : (
          <Table
            columns={columns}
            data={summary}
            hoverable
          />
        )}
      </Card>
    </div>
  );
}
